import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const empty = { name: '', type: 'monthly', price: '', features: [''], isActive: true, isPopular: false, color: '#00d4ff' };

export default function AdminMemberships() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [applicantModal, setApplicantModal] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [updating, setUpdating] = useState(false);

  const load = () => api.get('/memberships/admin')
    .then(({ data }) => {
      setMemberships(data.memberships || []);
      if (selectedMembership) {
        const updated = data.memberships.find(m => m._id === selectedMembership._id);
        if (updated) setSelectedMembership(updated);
      }
    })
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (m) => {
    setEditing(m._id);
    setForm({ name: m.name, type: m.type, price: m.price, features: m.features?.length ? m.features : [''], isActive: m.isActive, isPopular: m.isPopular, color: m.color || '#00d4ff' });
    setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, features: form.features.filter(f => f.trim()) };
      if (editing) await api.put(`/memberships/${editing}`, payload);
      else await api.post('/memberships', payload);
      setModal(false); load();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this membership plan?')) return;
    await api.delete(`/memberships/${id}`); load();
  };

  const updateFeature = (i, val) => {
    const f = [...form.features];
    f[i] = val;
    setForm({ ...form, features: f });
  };

  const handleApplicantStatus = async (membershipId, userId, status) => {
    setUpdating(true);
    try {
      await api.put(`/memberships/${membershipId}/applicant-status`, { userId, status, adminNote });
      setApplicantModal(null);
      setAdminNote('');
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    } finally {
      setUpdating(false);
    }
  };

  const pendingCount = memberships.reduce((acc, m) => acc + (m.applicants?.filter(a => a.status === 'pending').length || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>MEMBERSHIPS</h1>
          {pendingCount > 0 && <div style={{ fontSize: 13, color: '#f5c400', marginTop: 4 }}>⏳ {pendingCount} pending application{pendingCount > 1 ? 's' : ''} need review</div>}
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Plan</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedMembership ? '1fr 1fr' : '1fr', gap: 20 }}>
        {/* Membership List */}
        <div>
          {loading ? <div className="loading-spinner"><div className="spinner" /></div> :
            memberships.map(m => (
              <div key={m._id} className="card" style={{ marginBottom: 16, border: selectedMembership?._id === m._id ? '1px solid #f5c400' : '1px solid var(--border)', cursor: 'pointer' }}
                onClick={() => setSelectedMembership(selectedMembership?._id === m._id ? null : m)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: m.color }}>{m.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                      {m.price === 0 ? 'FREE' : `$${m.price}`} / {m.type} •
                      <span style={{ color: '#f5c400', marginLeft: 6 }}>{m.applicants?.filter(a => a.status === 'pending').length || 0} pending</span>
                      <span style={{ color: '#10b981', marginLeft: 6 }}>{m.applicants?.filter(a => a.status === 'approved').length || 0} approved</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: m.isActive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)', color: m.isActive ? '#10b981' : '#ef4444' }}>
                      {m.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => openEdit(m)}>Edit</button>
                    <button style={{ padding: '4px 12px', fontSize: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', cursor: 'pointer' }} onClick={() => handleDelete(m._id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))
          }
        </div>

        {/* Applicants Panel */}
        {selectedMembership && (
          <div className="card" style={{ height: 'fit-content' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: selectedMembership.color }}>
                {selectedMembership.name} — Applicants
              </h3>
              <button onClick={() => setSelectedMembership(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            {selectedMembership.applicants?.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No applicants yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {selectedMembership.applicants?.map((a, i) => (
                  <div key={i} style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 14, border: `1px solid ${a.status === 'pending' ? 'rgba(245,196,0,0.3)' : a.status === 'approved' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>{a.fullName || a.user?.username || 'Unknown'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(a.appliedAt).toLocaleDateString()}</div>
                      </div>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: a.status === 'approved' ? 'rgba(16,185,129,0.15)' : a.status === 'rejected' ? 'rgba(239,68,68,0.12)' : 'rgba(245,196,0,0.12)',
                        color: a.status === 'approved' ? '#10b981' : a.status === 'rejected' ? '#ef4444' : '#f5c400' }}>
                        {a.status}
                      </span>
                    </div>

                    {/* Applicant Details */}
                    <div style={{ fontSize: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
                      {a.email && <div><span style={{ color: 'var(--text-muted)' }}>Email: </span>{a.email}</div>}
                      {a.phone && <div><span style={{ color: 'var(--text-muted)' }}>Phone: </span>{a.phone}</div>}
                      {a.address && <div style={{ gridColumn: '1/-1' }}><span style={{ color: 'var(--text-muted)' }}>Address: </span>{a.address}</div>}
                      {a.telegramUsername && <div><span style={{ color: 'var(--text-muted)' }}>Telegram: </span>{a.telegramUsername}</div>}
                      {a.whatsappNumber && <div><span style={{ color: 'var(--text-muted)' }}>WhatsApp: </span>{a.whatsappNumber}</div>}
                      {a.chimeTag && <div><span style={{ color: 'var(--text-muted)' }}>Tag: </span>{a.chimeTag}</div>}
                      {a.paymentMethod && <div><span style={{ color: 'var(--text-muted)' }}>Payment: </span>{a.paymentMethod}</div>}
                    </div>

                    {/* Payment Screenshot */}
                    {a.paymentScreenshot && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Payment Screenshot:</div>
                        <img src={a.paymentScreenshot} alt="Payment" style={{ maxWidth: '100%', borderRadius: 8, maxHeight: 150, objectFit: 'contain', cursor: 'pointer' }}
                          onClick={() => window.open(a.paymentScreenshot, '_blank')} />
                      </div>
                    )}

                    {/* Action Buttons */}
                    {a.status === 'pending' && (
                      <button onClick={() => { setApplicantModal({ membershipId: selectedMembership._id, applicant: a }); setAdminNote(''); }}
                        className="btn btn-primary" style={{ width: '100%', fontSize: 13, padding: '8px' }}>
                        Review Application
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModal(false)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 20 }}>{editing ? 'Edit Plan' : 'Add Plan'}</h2>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Plan Name *</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="e.g. Gold Member" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} style={{ width: '100%', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text-primary)' }}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Price ($) — 0 for Free</label>
                  <input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required min="0" placeholder="0" />
                </div>
              </div>
              <div className="form-group">
                <label>Color</label>
                <input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} style={{ width: 60, height: 36, borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer' }} />
              </div>
              <div className="form-group">
                <label>Features</label>
                {form.features.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input value={f} onChange={e => updateFeature(i, e.target.value)} placeholder={`Feature ${i+1}`} style={{ flex: 1 }} />
                    {form.features.length > 1 && <button type="button" onClick={() => setForm({...form, features: form.features.filter((_,j) => j !== i)})} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', cursor: 'pointer', padding: '0 10px' }}>✕</button>}
                  </div>
                ))}
                <button type="button" onClick={() => setForm({...form, features: [...form.features, '']})} style={{ fontSize: 13, color: 'var(--accent-cyan)', background: 'none', border: 'none', cursor: 'pointer' }}>+ Add feature</button>
              </div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} />
                  Active
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={form.isPopular} onChange={e => setForm({...form, isPopular: e.target.checked})} />
                  Mark as Popular
                </label>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update Plan' : 'Create Plan'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Review Applicant Modal */}
      {applicantModal && (
        <div className="modal-overlay" onClick={() => setApplicantModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setApplicantModal(null)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 16 }}>Review Application</h2>
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>{applicantModal.applicant.fullName}</div>
              {applicantModal.applicant.email && <div style={{ color: 'var(--text-muted)' }}>📧 {applicantModal.applicant.email}</div>}
              {applicantModal.applicant.phone && <div style={{ color: 'var(--text-muted)' }}>📞 {applicantModal.applicant.phone}</div>}
              {applicantModal.applicant.address && <div style={{ color: 'var(--text-muted)' }}>📍 {applicantModal.applicant.address}</div>}
              {applicantModal.applicant.paymentMethod && <div style={{ color: 'var(--text-muted)', marginTop: 6 }}>💳 Payment: {applicantModal.applicant.paymentMethod}</div>}
            </div>
            <div className="form-group">
              <label>Admin Note (optional)</label>
              <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)} placeholder="Add a note for the user..." rows={3} style={{ width: '100%', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text-primary)', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => handleApplicantStatus(applicantModal.membershipId, applicantModal.applicant.user?._id || applicantModal.applicant.user, 'approved')}
                disabled={updating} style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
                ✅ Approve
              </button>
              <button onClick={() => handleApplicantStatus(applicantModal.membershipId, applicantModal.applicant.user?._id || applicantModal.applicant.user, 'rejected')}
                disabled={updating} style={{ flex: 1, padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
                ❌ Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
